/** The state-transport frames the overlay host exchanges with its one authority. */

import type {
  CanvasArrangement,
  Cue,
  CueFrame,
  DeltaFrame,
  Entity,
  OverlayFrame,
  OverlayHello,
  OverlayWelcome,
  ResyncRequest,
  RegionMap,
  SceneDocument,
  SnapshotFrame,
  StateDelta,
} from "@nebulize/sdk";

/** The canvas kind this build renders when the address names none. */
export const DEFAULT_CANVAS_KIND = "broadcast";

/** The frame schema version this host declares in its hello. */
export const OVERLAY_SCHEMA_VERSION = 1;

/** The four frames an authority sends a host, taken as a subset of the contract union. */
export type InboundFrame = Extract<
  OverlayFrame,
  { frame: "welcome" | "snapshot" | "delta" | "cue" }
>;

/** The state frames, which carry a sequence and are never shed. A cue is deliberately not one. */
export type StateFrame = Extract<InboundFrame, { frame: "welcome" | "snapshot" | "delta" }>;

/** Whether this frame belongs to the state machine. False for the one lossy frame. */
export function isStateFrame(frame: InboundFrame): frame is StateFrame {
  return frame.frame !== "cue";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isSequence(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

function isArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

/** A list-valued field is either absent or a list. Anything else makes the frame unreadable. */
function isAbsentOrArray(value: unknown): boolean {
  return value === undefined || isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return isArray(value) && value.every((member) => typeof member === "string");
}

/** An epoch is required on every state frame, so a frame carrying none is not readable. */
function isEpoch(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

/** The instant a frame says it was sent, absent on any body that was retained rather than pushed. */
function sentAt(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/** A welcome names the live mount, and nothing being mounted is the ordinary case. */
function asWelcome(raw: Record<string, unknown>): OverlayWelcome | undefined {
  if (raw.mount_id !== null && raw.mount_id !== undefined && typeof raw.mount_id !== "string") {
    return undefined;
  }
  if (!isStringArray(raw.canvases)) return undefined;
  if (!isSequence(raw.sequence)) return undefined;
  if (!isEpoch(raw.epoch)) return undefined;

  return {
    frame: "welcome",
    mount_id: (raw.mount_id) ?? null,
    canvases: raw.canvases,
    sequence: raw.sequence,
    epoch: raw.epoch,
  };
}

function asSnapshot(raw: Record<string, unknown>): SnapshotFrame | undefined {
  if (!isSequence(raw.sequence)) return undefined;
  if (!isEpoch(raw.epoch)) return undefined;
  if (!isAbsentOrArray(raw.entities) || !isAbsentOrArray(raw.scenes)) return undefined;
  if (!isAbsentOrArray(raw.regions)) return undefined;

  return {
    frame: "snapshot",
    sequence: raw.sequence,
    epoch: raw.epoch,
    sent_at: sentAt(raw.sent_at),
    entities: isArray(raw.entities) ? (raw.entities as Entity[]) : [],
    scenes: isArray(raw.scenes) ? (raw.scenes as SceneDocument[]) : [],
    regions: isArray(raw.regions) ? (raw.regions as RegionMap[]) : [],
    arrangement: asArrangement(raw.arrangement),
  };
}

/** The arrangement a snapshot carried, or nothing arranged. OVL-027.
 *
 * Shaped rather than trusted: an authority that sends no arrangement, an older one that has never
 * heard of the field, and one that sends something that is not an object all read the same way,
 * which is the state every space is in until somebody drags something.
 */
function asArrangement(raw: unknown): CanvasArrangement {
  if (raw === null || typeof raw !== "object") return { canvases: {} };
  const canvases = (raw as { canvases?: unknown }).canvases;
  if (canvases === null || typeof canvases !== "object") return { canvases: {} };
  return { canvases: canvases as NonNullable<CanvasArrangement["canvases"]> };
}

function asDelta(raw: Record<string, unknown>): DeltaFrame | undefined {
  if (!isSequence(raw.sequence)) return undefined;
  if (!isEpoch(raw.epoch)) return undefined;
  if (!isAbsentOrArray(raw.deltas)) return undefined;

  return {
    frame: "delta",
    sequence: raw.sequence,
    epoch: raw.epoch,
    sent_at: sentAt(raw.sent_at),
    deltas: isArray(raw.deltas) ? (raw.deltas as StateDelta[]) : [],
  };
}

/** A cue names its tag and who may see it. Everything else on it is optional presentation. */
function asCue(raw: Record<string, unknown>): CueFrame | undefined {
  if (typeof raw.envelope_id !== "string" || raw.envelope_id.length === 0) return undefined;

  const cue = raw.cue;
  if (!isRecord(cue)) return undefined;
  if (typeof cue.tag !== "string" || cue.tag.length === 0) return undefined;
  if (!isStringArray(cue.canvases)) return undefined;

  return {
    frame: "cue",
    envelope_id: raw.envelope_id,
    cue: cue as unknown as Cue,
  };
}

/** Take one JSON message as the frame its tag names, or as nothing at all. */
export function classifyFrame(raw: unknown): InboundFrame | undefined {
  if (!isRecord(raw)) return undefined;

  switch (raw.frame) {
    case "welcome":
      return asWelcome(raw);
    case "snapshot":
      return asSnapshot(raw);
    case "delta":
      return asDelta(raw);
    case "cue":
      return asCue(raw);
    default:
      return undefined;
  }
}

/** Whether a store at `current` in `epoch` adopts this snapshot. Another epoch always wins. */
export function snapshotSupersedes(
  frame: SnapshotFrame,
  current: number | undefined,
  epoch: string | undefined,
): boolean {
  if (epoch !== undefined && epoch !== frame.epoch) return true;
  return current === undefined || frame.sequence > current;
}

/** Whether a store at `current` in `epoch` may apply this delta. False for an empty store. */
export function deltaFollows(
  frame: DeltaFrame,
  current: number | undefined,
  epoch: string | undefined,
): boolean {
  if (epoch === undefined || epoch !== frame.epoch) return false;
  return current !== undefined && frame.sequence === current + 1;
}

/** Whether this delta is one the store already applied. Never true across an epoch. */
export function deltaIsRedelivery(
  frame: DeltaFrame,
  current: number | undefined,
  epoch: string | undefined,
): boolean {
  if (epoch === undefined || epoch !== frame.epoch) return false;
  return current !== undefined && frame.sequence <= current;
}

export function overlayHello(token: string, canvasKind: string = DEFAULT_CANVAS_KIND): OverlayHello {
  return {
    frame: "hello",
    canvas_token: token,
    canvas_kind: canvasKind,
    schema_version: OVERLAY_SCHEMA_VERSION,
  };
}

export function resyncRequest(
  reason: string,
  lastSequence: number | undefined,
  lastEpoch: string | undefined,
): ResyncRequest {
  return {
    frame: "resync",
    reason,
    last_sequence: lastSequence ?? null,
    last_epoch: lastEpoch ?? null,
  };
}

/** Parse one socket message, treating anything unparseable as no frame at all. */
export function parseFrame(data: string): InboundFrame | undefined {
  let raw: unknown;
  try {
    raw = JSON.parse(data) as unknown;
  } catch {
    return undefined;
  }
  return classifyFrame(raw);
}
